import { useMemo, useState, type FormEvent } from "react";
import { errorMessage } from "@/lib/api";
import type { CoachingVisibility } from "@/lib/types";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useToast } from "@/components/ui/toast";
import { CoachModal } from "./CoachModal";
import { NOTE_CATEGORIES, NOTE_CATEGORY_LABELS } from "@/features/coach-review/components/Composer";
import { useQuickFeedback } from "../hooks/useQuickFeedback";
import { useRoster } from "../hooks/useRoster";

const TEAM_VALUE = "__team__";

const VISIBILITY_OPTIONS: ReadonlyArray<{
  value: CoachingVisibility;
  label: string;
}> = [
  { value: "player", label: "Player + family" },
  { value: "team", label: "Team" },
  { value: "private", label: "Coach-only" },
  { value: "unlisted", label: "Unlisted" },
];

export function QuickFeedbackModal({
  initialPlayerId,
  onClose,
  onOpenCoachReview,
}: {
  initialPlayerId?: string;
  onClose: () => void;
  onOpenCoachReview: (playerId?: string) => void;
}) {
  const toast = useToast();
  const rosterQuery = useRoster();
  const mutation = useQuickFeedback();
  const players = rosterQuery.data?.players ?? [];
  const initialRecipient = initialPlayerId || TEAM_VALUE;
  const [recipient, setRecipient] = useState(initialRecipient);
  const [keyMessage, setKeyMessage] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [evidence, setEvidence] = useState("");
  const [category, setCategory] =
    useState<(typeof NOTE_CATEGORIES)[number]>("other");
  const [visibility, setVisibility] = useState<CoachingVisibility>(
    initialPlayerId ? "player" : "team",
  );
  const [coachPrivateNote, setCoachPrivateNote] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPlayer = useMemo(
    () => players.find((player) => player.id === recipient),
    [players, recipient],
  );
  const playerId = recipient === TEAM_VALUE ? undefined : recipient;
  const canSubmit = keyMessage.trim().length > 0 && nextStep.trim().length > 0;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      setError("Add a key message and a next step.");
      return;
    }
    const title = selectedPlayer
      ? `Feedback for ${selectedPlayer.display_name}`
      : "Team feedback";
    try {
      await mutation.mutateAsync({
        match_id: null,
        slot: null,
        timestamp_seconds: null,
        title,
        body: evidence.trim(),
        category,
        visibility,
        player_ids: playerId ? [playerId] : [],
        tags: ["quick-feedback"],
        drawing: {},
        note_type: "correction",
        what_happened: keyMessage.trim(),
        why_it_matters: "",
        what_to_do_next: nextStep.trim(),
        player_summary: keyMessage.trim(),
        coach_private_note: coachPrivateNote.trim(),
        note_context: "observation",
        event_title: "",
        event_date: "",
        event_type: "",
        tactical_board_json: null,
      });
      toast.success("Feedback saved");
      onClose();
    } catch (err) {
      const message = errorMessage(err);
      setError(message);
      toast.error("Couldn't save feedback", { description: message });
    }
  };

  return (
    <CoachModal
      title="Give feedback"
      onClose={onClose}
      data-testid="quick-feedback-modal"
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Who is this for?
          </span>
          <TkticSelect
            value={recipient}
            onChange={(next) => {
              setRecipient(next);
              if (next === TEAM_VALUE && visibility === "player") {
                setVisibility("team");
              }
            }}
            ariaLabel="Feedback recipient"
            data-testid="quick-feedback-recipient"
            options={[
              { value: TEAM_VALUE, label: "Team feedback" },
              ...players.map((player) => ({
                value: player.id,
                label: player.jersey_number
                  ? `#${player.jersey_number} ${player.display_name}`
                  : player.display_name,
              })),
            ]}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Key message
          </span>
          <textarea
            value={keyMessage}
            onChange={(event) => setKeyMessage(event.target.value)}
            rows={3}
            data-testid="quick-feedback-message"
            className="min-h-24 rounded border border-rule bg-bg-card px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="What should the player understand?"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            What to do next
          </span>
          <textarea
            value={nextStep}
            onChange={(event) => setNextStep(event.target.value)}
            rows={3}
            data-testid="quick-feedback-next-step"
            className="min-h-24 rounded border border-rule bg-bg-card px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Name the next action or cue."
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
            Evidence
            <span className="ml-1 normal-case tracking-normal text-ink-muted/70">
              (optional)
            </span>
          </span>
          <textarea
            value={evidence}
            onChange={(event) => setEvidence(event.target.value)}
            rows={2}
            data-testid="quick-feedback-evidence"
            className="min-h-20 rounded border border-rule bg-bg-card px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Describe a clip, match moment, note, or observation."
          />
          <button
            type="button"
            onClick={() => onOpenCoachReview(playerId)}
            className="mt-2 self-start rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
            data-testid="quick-feedback-open-review"
          >
            Add richer evidence in Review
          </button>
        </label>

        <button
          type="button"
          onClick={() => setAdvancedOpen((open) => !open)}
          aria-expanded={advancedOpen}
          className="self-start rounded border border-rule px-3 py-1.5 text-xs font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
          data-testid="quick-feedback-advanced-toggle"
        >
          Advanced
        </button>

        {advancedOpen ? (
          <div
            className="grid gap-3 rounded border border-rule bg-bg-card p-3"
            data-testid="quick-feedback-advanced"
          >
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Category
              </span>
              <TkticSelect
                value={category}
                onChange={(next) =>
                  setCategory(next as (typeof NOTE_CATEGORIES)[number])
                }
                ariaLabel="Feedback category"
                data-testid="quick-feedback-category"
                options={NOTE_CATEGORIES.map((value) => ({
                  value,
                  label: NOTE_CATEGORY_LABELS[value],
                }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Visibility
              </span>
              <TkticSelect
                value={visibility}
                onChange={(next) => setVisibility(next as CoachingVisibility)}
                ariaLabel="Feedback visibility"
                data-testid="quick-feedback-visibility"
                options={VISIBILITY_OPTIONS.map((option) => ({
                  ...option,
                  disabled: option.value === "player" && !playerId,
                }))}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-xs font-display uppercase tracking-wider text-ink-muted">
                Coach-only note
              </span>
              <textarea
                value={coachPrivateNote}
                onChange={(event) => setCoachPrivateNote(event.target.value)}
                rows={2}
                data-testid="quick-feedback-private-note"
                className="min-h-20 rounded border border-rule bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
                placeholder="Private coaching context."
              />
            </label>
          </div>
        ) : null}

        {error ? (
          <p className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-ink">
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2 border-t border-rule pt-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule px-4 py-2 text-sm font-display uppercase tracking-wider text-ink hover:bg-bg-card transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || mutation.isPending}
            data-testid="quick-feedback-submit"
            className="rounded bg-accent px-4 py-2 text-sm font-display uppercase tracking-wider text-white hover:bg-accent-hover disabled:bg-bg-card disabled:text-ink-muted transition-colors"
          >
            {mutation.isPending ? "Saving..." : "Save feedback"}
          </button>
        </div>
      </form>
    </CoachModal>
  );
}
